import React, { useRef } from "react";
import { DrupalMenuItem } from "@/types/shared/DrupalMenuItem";
import { pkceUserProfile } from "@/types/auth/pkceUserProfile";
import { ClickAwayListener } from "@mui/base";
import { theme } from "@/config/theme";
import styled from "@emotion/styled";
import Typography from "@digital-hig/mui/Typography";
import { DESKTOP_HEADER_MEDIA_QUERY, MOBILE_HEADER_MEDIA_QUERY } from "./HeaderConstants";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuItemButton,
  DropdownMenuItemDescription,
  DropdownProfileSection
} from "@/components/Header/MenuComponents";
import { EllipsisText } from "../EllipsisText";
import Button from "@digital-hig/mui/Button";

const ProfileMenuToggle = styled.button({
  borderRadius: 1000,
  backgroundColor: theme.primitives.colors.accent.iris,
  backgroundSize: "cover",
  backgroundPosition: "center",
  height: 32,
  width: 32,
  position: "relative",
  appearance: "none",
  border: "none",
  "&:focus-visible": {
    outline: `1.5px solid ${theme.primitives.colors.accent.iris}`,
    outlineOffset: 1.5
  }
});

const DropdownTopTriangle = styled.div({
  "&:before": {
    content: '""',
    borderBottom: "8px solid #fff",
    borderLeft: "9px solid transparent",
    borderRight: "9px solid transparent",
    display: "inline-block",
    position: "absolute",
    right: "0.5rem",
    top: "calc(100% + 0.25rem)",
    zIndex: 1001
  }
});

interface ProfileNavProps {
  isOpen: boolean;
}

const ProfileNav = styled.nav<ProfileNavProps>(({ isOpen }) => ({
  display: isOpen ? "block" : "none",
  [MOBILE_HEADER_MEDIA_QUERY]: {
    display: "flex",
    flexDirection: "column",
    background: theme.primitives.colors.primary.white,
    width: isOpen ? "16.25rem" : 0,
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0
  }
}));

export function ProfileMenu({
  constrainHeight,
  maxConstrainedHeight,
  userProfile,
  // menuItems,
  isOpen,
  onOpen: handleOpen,
  onClose: handleClose,
  signOut
}: {
  constrainHeight: boolean;
  maxConstrainedHeight: string | number;
  // menuItems: DrupalMenuItem[];
  userProfile: pkceUserProfile;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  signOut: () => void;
}) {
  const firstNameInitial = firstLetter(userProfile.firstName);
  const lastNameInitial = firstLetter(userProfile.lastName);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLUListElement>(null);

  const profileImage = userProfile.profileImages?.sizeX80;
  const hasProfileImage =
    // The default image looks bad, so if it is present we'll use our own.
    !!profileImage; /*&& !profileImage.includes("/default/user_")*/

  function firstLetter(string: string) {
    if (!string) return "";
    return string.length > 0 ? string.split("")[0] : "";
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div
        ref={wrapperRef}
        css={{
          position: "relative",
          zIndex: 10,
          marginRight: theme.primitives.spacings[1]
        }}
      >
        <ProfileMenuToggle
          aria-label="Toggle Profile Menu"
          aria-expanded={isOpen}
          style={{
            backgroundImage: hasProfileImage ? `url(${profileImage})` : "none",
            width: "36px",
            height: "36px"
          }}
          onClick={isOpen ? handleClose : handleOpen}
        >
          {!hasProfileImage ? (
            <div
              css={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)"
              }}
            >
              <Typography css={{ color: "#fff" }}>{firstNameInitial + lastNameInitial}</Typography>
            </div>
          ) : null}
        </ProfileMenuToggle>

        <DropdownTopTriangle css={{ display: isOpen ? "block" : "none" }} />

        <ProfileNav isOpen={isOpen} data-testid="profile-menu">
          <Dropdown
            isOpen={isOpen}
            constrainHeight={constrainHeight}
            maxConstrainedHeight={maxConstrainedHeight}
            displayAsSlideOutOnMobile={true}
            css={{
              display: "flex",
              flexDirection: "column",
              [DESKTOP_HEADER_MEDIA_QUERY]: {
                top: "calc(100% + 0.65rem) !important",
                right: "calc(100% - 32px)",
                left: "initial",
                zIndex: 1000
              }
            }}
          >
            <DropdownMenu
              data-testid="profile-menu"
              ref={dropdownMenuRef}
              tabIndex={-1}
              direction="column"
              css={{ overflowY: "auto", border: "1px solid lightgrey" }}
            >
              <DropdownMenuItem>
                <DropdownProfileSection displayAsSlideOutOnMobile={true}>
                  <EllipsisText
                    css={{
                      fontWeight: 600
                    }}
                  >
                    {userProfile.firstName} {userProfile.lastName}
                  </EllipsisText>
                  <DropdownMenuItemDescription as="div">
                    <EllipsisText>{userProfile.emailId}</EllipsisText>
                  </DropdownMenuItemDescription>
                </DropdownProfileSection>
              </DropdownMenuItem>
              <DropdownMenuItem>
                {/* <DropdownMenuItemButton
                                    data-testid="sign-out-button"
                                    onClick={signOut}
                                    displayAsSlideOutOnMobile={true}
                                >
                                    Sign Out
                                </DropdownMenuItemButton> */}
                <div style={{ marginLeft: "10px", marginRight: "10px", marginBottom: "10px" }}>
                  <Button onClick={signOut} variant="outlined" sx={{ width: "100%" }}>
                    Sign Out
                  </Button>
                </div>
              </DropdownMenuItem>
            </DropdownMenu>
          </Dropdown>
        </ProfileNav>
      </div>
    </ClickAwayListener>
  );
}
